<template>
    <div v-if="loaded">
        <rkm-form
            ref="Form"
            class="mb-2"
        >
            <rkm-row>
                <rkm-col cols="2">
                    <rkm-form-select
                        :options="form.periodoOrigens"
                        @change="loadFrom('periodoOrigem')"
                        label="Período (protocolo)"
                        v-model="model.periodoOrigem"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-date-picker
                        :end-validations="validations.periodo"
                        :max-date="additionals.dataAtual"
                        :range-inline="false"
                        :start-validations="validations.periodo"
                        end-label="Até"
                        range
                        start-label="De"
                        v-model="model.periodo"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :default-option-text="isPeriodoPreenchido ? 'Todas' : 'Selecione'"
                        :options="form.unidadesExecutoras"
                        :validations="validations.unidadeExecutora"
                        @change="loadFrom('unidadeExecutora')"
                        label="Unidade executora"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeExecutoraId"
                        value-field='id'
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :disabled="!isRequiredFieldsPreenchidos"
                        :repository="repositories.monitoramentoProtocolo.usuarioResponsavel"
                        :request-query-string-params="filters"
                        :search-fields="usuarioResponsavelSearchFields"
                        @select="loadFrom('usuarioResponsavel')"
                        label="Profissional responsável"
                        placeholder="Nome"
                        text-field="nome"
                        v-model="model.usuarioResponsavel"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :disabled="!isRequiredFieldsPreenchidos"
                        :repository="repositories.monitoramentoProtocolo.paciente"
                        :request-query-string-params="filters"
                        :search-fields="pacienteSearchFields"
                        @select="loadFrom('paciente')"
                        label="Paciente"
                        placeholder="Nome, CNS, CPF, Data de nascimento ou Prontuário"
                        text-field="nome"
                        v-model="model.paciente"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-multiselect
                        :disabled="!isRequiredFieldsPreenchidos"
                        :options="form.cids"
                        :select-limit="null"
                        @update:dropdownClosed="loadFrom('cid')"
                        label="CID"
                        option-name-key="descricao"
                        option-text-selecteds="CID selecionado"
                        v-model="model.cids"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-select
                        :disabled="!isRequiredFieldsPreenchidos"
                        :options="form.situacoesProtocolos"
                        @change="loadFrom('situacaoProtocolo')"
                        default-option-text="Todas"
                        label="Situação do protocolo"
                        show-default-option
                        v-model="model.situacaoProtocolo"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :disabled="!isRequiredFieldsPreenchidos"
                        :validations="validations.linhaCuidado"
                        @change="loadFrom('linhaCuidado')"
                        label="Linha de cuidado"
                        v-model="model.linhaCuidado"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :disabled="!isRequiredFieldsPreenchidos"
                        :options="form.etapasClinicasAtuais"
                        default-option-text="Todas"
                        label="Etapa clínica atual"
                        show-default-option
                        text-field="nome"
                        v-model="model.etapaClinicaAtualId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        @click="$refs.Form.validate($refs.DataTable.search)"
                        color="primary"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetFilters"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>
            </template>

            <rkm-data-table
                :fields="fields"
                :repository="repositories.monitoramentoProtocolo"
                :request-params="filters"
                :tbody-tr-class="rowClass"
                class="tabela-resultado"
                hide-filters
                ref="DataTable"
                sort-by="dataInicio"
            >
                <template v-slot:cell(cids)="{ item }">
                    <div
                        v-for="(cid, i) in item.cids"
                        :key="i"
                    >{{ cid }}</div>
                </template>

                <template v-slot:cell(statusNome)="{ item }">
                    {{ item.statusNome }}

                    <rkm-icon
                        v-if="item.diasAtrasado"
                        :title="`Atrasado à ${item.diasAtrasado} dia(s)`"
                        icon="exclamation-triangle"
                    />
                </template>

                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        @click="$refs.ModalDetalhe.open(item)"
                        color="primary"
                        small
                        title="Exibir detalhes"
                    >
                        <rkm-icon icon="bars" />
                    </rkm-button>

                    <rkm-button
                        v-if="verificarPermissao('show-ambulatorio-historico-atendimento')"
                        @click="$refs.ModalAtendimento.open(item)"
                        color="info"
                        small
                        title="Visualizar atendimentos"
                    >
                        <rkm-icon icon="eye" />
                    </rkm-button>

                    <rkm-button
                        @click="$refs.ModalSolicitacao.open(item)"
                        color="warning"
                        small
                        title="Visualizar solicitações"
                    >
                        <rkm-icon icon="eye" />
                    </rkm-button>

                    <rkm-button
                        :disabled="item.isConcluido"
                        @click="$refs.ModalEncerramento.open(item)"
                        color="success"
                        small
                        title="Encerrar OCI"
                    >
                        <rkm-icon icon="check" />
                    </rkm-button>
                </template>

                <template #afterTable>
                    <div class="my-2">
                        <p>
                            A linha em <strong class="green">verde</strong> representa que o protocolo foi concluído.<br>
                            A linha em <strong class="red">vermelho</strong> representa que o protocolo está com prazo vencido.<br>
                            A linha em <strong class="purple">roxo</strong> representa que o paciente está em óbito.
                        </p>
                    </div>
                </template>
            </rkm-data-table>
        </rkm-area>

        <ModalDetalhe ref="ModalDetalhe" />
        <ModalAtendimento ref="ModalAtendimento" />
        <ModalSolicitacao ref="ModalSolicitacao" />

        <ModalEncerramento
            ref="ModalEncerramento"
            @save="$refs.DataTable.search()"
        />
    </div>
</template>

<script>
// Components.
import ModalDetalhe from './components/detalhe/Modal'
import ModalAtendimento from './components/atendimento/Modal'
import ModalSolicitacao from './components/solicitacao/Modal'
import ModalEncerramento from './components/encerramento/Modal'

// Mixins.
import { repository, error, stringCase, permissao } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

export default {
    components: {
        ModalDetalhe,
        ModalAtendimento,
        ModalSolicitacao,
        ModalEncerramento,
    },
    computed: {
        validations() {

            return {
                periodo: { required: !this.isUnidadeExecutoraPreenchida },
                unidadeExecutora: { required: !this.isPeriodoPreenchido },
                linhaCuidado: { max: 100 },
            }
        },
        isRequiredFieldsPreenchidos() {

            return this.isPeriodoPreenchido || this.isUnidadeExecutoraPreenchida
        },
        isPeriodoPreenchido() {

            return this.model.periodo
                && !!this.model.periodo.start
                && !!this.model.periodo.end
        },
        isUnidadeExecutoraPreenchida() {

            return !!this.model.unidadeExecutoraId
        },
        filters() {

            return this.camelObjectKeysToSnakeRecursive({
                periodoOrigem: this.model.periodoOrigem,
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeExecutoraId: this.model.unidadeExecutoraId,
                usuarioResponsavelId: this.model.usuarioResponsavel?.id ?? null,
                pacienteId: this.model.paciente?.id ?? null,
                cids: this.model.cids,
                situacaoProtocolo: this.model.situacaoProtocolo,
                linhaCuidado: this.model.linhaCuidado,
                etapaClinicaAtualId: this.model.etapaClinicaAtualId
            })
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        columns() {

            return [
                { key: 'dataInicio', name: 'acompanhamentoOci.iniciadoEm', label: 'Data de início', sortable: true, fixed: true },
                { key: 'dataFinalizacao', name: 'acompanhamentoOci.finalizadoEm', label: 'Data de encerramento', sortable: true },
                { key: 'pacienteProntuario', name: 'paciente.prontuario', label: 'Prontuário', sortable: true },
                { key: 'pacienteNome', name: 'pacienteCidadao.nome', label: 'Paciente', sortable: true },
                { key: 'pacienteDataNascimento', name: 'pacienteCidadao.dataNascimento', label: 'Data de nascimento', sortable: true },
                { key: 'pacienteIdade', name: 'pacienteIdade', label: 'Idade', sortable: true },
                { key: 'pacienteSexoDescricao', name: 'sexo.descricao', label: 'Sexo', sortable: true },
                { key: 'linhaCuidado', name: 'oci.linhaCuidado', label: 'Linha de cuidado', sortable: true },
                { key: 'cids', name: 'cids', label: 'CID(s)', sortable: true },
                { key: 'usuarioResponsavelNome', name: 'usuarioCidadao.nome', label: 'Profissional responsável', sortable: true },
                { key: 'unidadeExecutoraNomeFantasia', name: 'unidade.nomeFantasia', label: 'Unidade executora', sortable: true },
                { key: 'etapaClinicaAtualNome', name: 'etapaClinicaAtual.nome', label: 'Etapa clínica atual', sortable: true },
                { key: 'validade', name: 'validade', label: 'Validade', sortable: true },
                { key: 'prazoRestante', name: 'prazoRestante', label: 'Prazo restante (dias)', class: 'text-center', sortable: true },
                { key: 'statusNome', name: 'status.nome', label: 'Status', sortable: true },
                { key: 'actions', label: 'Ações', class: 'col-min text-center text-nowrap', fixed: true },
            ]
        },
        usuarioResponsavelSearchFields() {

            return [
                { key: 'cidadao.nome', searchable: true },
            ]
        },
        pacienteSearchFields() {

            return [
                { key: 'cidadao.nome', searchable: true },
                { key: 'cidadao.cns', searchable: true },
                { key: 'cidadao.cpf', searchable: true },
                { key: 'cidadao.data_nascimento', searchable: true },
                { key: 'paciente.prontuario', searchable: true },
            ]
        },
    },
    created() {

        this.repositories = {
            monitoramentoProtocolo: this.getConectaSusRepository('regulacao.monitoramentoProtocolo')
        }

        this.load()
    },
    data: () => ({
        selectedAll: false,
        indeterminate: true,
        selectedCols: [
            'dataFinalizacao',
            'pacienteProntuario',
            'pacienteNome',
            'pacienteIdade',
            'linhaCuidado',
            'cid',
            'usuarioResponsavelNome',
            'etapaClinicaAtualNome',
            'validade',
            'prazoRestante',
            'statusNome',
        ],
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        unwatchers: {
            unwatchPeriodo: null,
            unwatchUsuarioResponsavel: null,
            unwatchPaciente: null,
        },
        others: { moment },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.monitoramentoProtocolo.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.$nextTick().then(() => this.addWatchers())

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.unwatchers.unwatchPeriodo = this.$watch('model.periodo', {
                deep: true,
                handler() {

                    this.loadFrom('periodo')
                }
            })

            this.unwatchers.unwatchUsuarioResponsavel = this.$watch('model.usuarioResponsavel', usuario => {

                if (!usuario) {

                    this.loadFrom('usuarioResponsavel')
                }
            })

            this.unwatchers.unwatchPaciente = this.$watch('model.paciente', paciente => {

                if (!paciente) {

                    this.loadFrom('paciente')
                }
            })
        },
        loadFrom(field) {

            this.setLoading()

            this.listFrom(field)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        async listFrom(field) {

            const { data } = await this.repositories.monitoramentoProtocolo.list({ ...this.filters, load_from: this.camelToSnake(field) })

            if ('form' in data) {

                Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
            }

            Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
        },
        resetFilters() {

            this.unwatchers.unwatchPeriodo()
            this.unwatchers.unwatchUsuarioResponsavel()
            this.unwatchers.unwatchPaciente()

            this.$nextTick().then(() => this.load())
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        rowClass(item, type) {

            if (type === 'row' && item) {

                if (item.isPacienteEmObito) {

                    return 'table-purple'
                }
                else if (item.isConcluido) {

                    return 'table-success'
                }
                else if (item.isVencido) {

                    return 'table-danger'
                }

                return ''
            }
        },
    },
    mixins: [repository, error, stringCase, permissao],
    watch: {
        model: {
            deep: true,
            handler() {

                this.$nextTick().then(() => this.$refs.DataTable.clearTable())
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.tabela-resultado >>> .green {
    color: green;
}

.tabela-resultado >>> .red {
    color: red;
}

.tabela-resultado >>> .purple {
    color: #733380;
}

.tabela-resultado >>> .table-purple {
    border-color: #a275a9;
    background-color: #e8dbe9;
}
</style>